
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database['public']['Enums']['app_role'];
type SubscriptionTier = Database['public']['Enums']['subscription_tier'];

interface UserData {
  id: string;
  email: string;
  roles: AppRole[];
  subscription_tier: SubscriptionTier;
}

export function UserList() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      // Get user emails using our secure function
      const { data: emailsData, error: emailsError } = await supabase
        .rpc('get_user_emails');

      if (emailsError) {
        console.error('Error fetching user emails:', emailsError);
        throw emailsError;
      }

      // Get all roles
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('*');

      if (rolesError) {
        console.error('Error fetching roles:', rolesError);
        throw rolesError;
      }

      // Get all profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('*');

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        throw profilesError;
      }

      // Map the data using emails as the base
      return emailsData.map(emailData => ({
        id: emailData.id,
        email: emailData.email,
        roles: rolesData
          .filter(r => r.user_id === emailData.id)
          .map(r => r.role as AppRole),
        subscription_tier: profilesData.find(p => p.id === emailData.id)?.subscription_tier || 'basic'
      })) as UserData[];
    }
  });

  const updateRole = useMutation({
    mutationFn: async ({ userId, role }: { userId: string, role: AppRole }) => {
      // Delete existing roles
      const { error: deleteError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      // Insert new role
      const { error: insertError } = await supabase
        .from('user_roles')
        .insert({ user_id: userId, role });

      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: "Success",
        description: "User role updated successfully",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update user role: " + error.message,
      });
    }
  });

  if (isLoading) {
    return <div>Loading users...</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Email</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Subscription</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users?.map((user) => (
          <TableRow key={user.id}>
            <TableCell>{user.email}</TableCell>
            <TableCell>
              <Select
                value={user.roles[0] || 'user'}
                onValueChange={(newRole: AppRole) => {
                  updateRole.mutate({ userId: user.id, role: newRole });
                }}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="superadmin">Superadmin</SelectItem>
                </SelectContent>
              </Select>
            </TableCell>
            <TableCell>{user.subscription_tier}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

